<template>
  <b-modal
    ref="notificationModal"
    size="xl"
    centered
    modal-class="notification-modal"
    qa="notification-modal"
    @hide="gridApi.deselectAll()"
    @shown="onNotificationModalShown"
  >
    <div slot="modal-title">
      <span v-if="type === NOTIFICATION_TYPE.NOTIFICATIONS">{{ $tc('global.notification',2) }}</span>
      <span v-else-if="type === NOTIFICATION_TYPE.ALARMS">{{ $tc('global.alarm',2) }}</span>
    </div>
    <b-row class="px-1">
      <b-col>
        <div class="grid-container mb-0">
          <GridHeader
            :gridtitle="$t('notifications.notificationsList')"
            :gridsubtitle="msgSelectedRows"
          >
            <grid-display-options
              v-if="gridApi"
              :key="uniqueId"
              ref="notificationModalGridDisplayOptions"
              :ui-component="displayOptionsUiComponent"
              @changedPageSize="changedPageSize($event)"
              @changedMode="changedMode($event)"
            />
            <div class="pl-1 pr-0 d-flex align-items-center">
              <button
                v-tooltip="$t('global.cleanFilter')"
                type="button"
                class="btn btn__icon btn__icon--primary mx-2 p-0 btn-secondary"
                @click="clearFilters"
              >
                <i
                  aria-hidden="true"
                  class="far fa-filter"
                />
              </button>
              <vt-loading-button
                class="btn btn__icon btn__icon--primary mx-2 p-0 btn-secondary"
                :params="{ promise: markAsRead }"
              >
                <i
                  v-tooltip="$t('notifications.markAllAsRead')"
                  class="far fa-check-double"
                  aria-hidden="true"
                />
              </vt-loading-button>
            </div>
          </GridHeader>
          <vt-grid
            ref="notificationModalVtGrid"
            :key="uniqueId"
            :grid-options="gridOptions"
            :component-code="uiComponent"
            class="notifications-grid"
            :column-defs="columnDefs"
            @selection-changed="onSelectionChanged($event)"
          />
        </div>
      </b-col>
    </b-row>
    <div
      slot="modal-footer"
      class="footer-banner px-2"
      style="width:100%;"
    >
      <div class="row">
        <div class="col-6 text-left">
          <b-button
            size="sm"
            variant="outline-secondary"
            data-dismiss="modal"
            @click="hide"
          >
            {{ $t('global.cancel') }}
          </b-button>
        </div>
      </div>
    </div>
    <notification-detail-modal
      ref="notificationDetailModal"
      @reload="loadNotifications"
    />
  </b-modal>
</template>

<script>
import GridHeader from '@/components/GridHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import columnsNotificationGrid from '@/components/NavBar/notifications/config/columnsNotificationGrid'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import GridDisplayOptions from '@/components/GridDisplayOptions'
import NotificationDetailModal from '@/components/NavBar/notifications/NotificationDetailModal'
import notificationService from '@/components/NavBar/notifications/services/notificationService'
import { NotificationsGridDatasource } from '@/components/NavBar/notifications/config/notifications-grid-datasource'
import { NOTIFICATION_TYPE } from '@/components/NavBar/notifications/constants/notificationTypes'
import { uniqueId } from 'lodash'
import { AgGridRowModelType } from '@/shared/models/ag-grid.model'

export default {
    name: 'NotificationModal',
    components: {
        VtGrid,
        GridHeader,
        NotificationDetailModal,
        GridDisplayOptions
    },
    props: {
        type: { type: String, required: true }
    },
    data() {
        return {
            uiComponent: 'uicomponent.notifications.filter',
            displayOptionsUiComponent: 'uicomponent.notifications.grid',
            mode: { value: 'compact', icon: 'fal fa-align-justify' },
            pageSize: 20,
            markAllAsReadButton: false,
            rowData: [],
            columnDefs: columnsNotificationGrid(),
            selectedRows: [],
            gridApi: null,
            uniqueId: uniqueId(this.type)
        }
    },
    computed: {
        gridOptions() {
            return {
                onGridReady: params => this.onGridReady(params),
                getRowHeight: (params) => this.rowHeight(params),
                paginationPageSize: this.pageSize,
                paginationAutoPageSize: false,
                sideBar: gridSideBarConfig,
                rowModelType: AgGridRowModelType.SERVER_SIDE,
                onRowDoubleClicked: this.showNotificationDetailModal,
                getRowStyle: (params) => {
                    if (params.data && params.data.read === true) {
                        return { background: '#CBF1E3 !important' }
                    }
                }
            }
        },
        msgSelectedRows() {
            let msg = this.selectedRows.length > 1 ? `${this.selectedRows.length} ${this.$tc('global.selectedElements', 2)}` : `${this.selectedRows.length} ${this.$tc('global.selectedElements', 1)}`
            if (this.selectedRows.length === 0) msg = ''
            return msg
        },
        NOTIFICATION_TYPE() {
            return NOTIFICATION_TYPE
        }
    },
    methods: {
        generateUniqueId() {
            this.uniqueId = uniqueId(this.type)
        },
        onNotificationModalShown() {
            if (this.$refs.notificationModalGridDisplayOptions) {
                this.$refs.notificationModalGridDisplayOptions.loadCustomConfig()
            }
            if (this.$refs.notificationModalVtGrid) {
                this.$refs.notificationModalVtGrid.loadCustomConfig()
            }
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.getRowData()
        },
        changedPageSize(pageSize) {
            this.pageSize = pageSize
            this.gridApi.paginationSetPageSize(pageSize)
        },
        onSelectionChanged() {
            this.selectedRows = this.gridApi.getSelectedRows()
        },
        show() {
            this.generateUniqueId()
            this.$refs.notificationModal.show()
        },
        hide() {
            this.$refs.notificationModal.hide()
        },
        showNotificationDetailModal({ data }) {
            this.$refs.notificationDetailModal.show(data)
        },
        loadNotifications() {
            this.getRowData()
            this.$emit('reload')
        },
        onGridReady(api) {
            this.gridApi = api.api
            this.columnApi = api.columnApi
            this.getRowData()
        },
        getRowData() {
            this.gridApi.setServerSideDatasource(
                new NotificationsGridDatasource(this.type === NOTIFICATION_TYPE.ALARMS))
        },
        async markAsRead() {
            const [err] = await this.toCall(notificationService.updateAllNotificationAsRead(this.type === NOTIFICATION_TYPE.ALARMS))
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
            }
            await this.getRowData()
            this.showToastSuccessGlobal(this.$t('notifications.allNotificationsMarkedAsRead'))
            this.$emit('reload')
        },
        rowHeight(params) {
            if (params?.node && params?.node.detail) {
                return 280
            } else {
                if (this.mode.value === 'compact') {
                    return 25
                }
                return 40
            }
        },
        clearFilters() {
            this.gridApi.setFilterModel(null)
        }
    }
}
</script>

<style lang="scss">
.notification-modal .notifications-grid {
  height: calc(100vh - 330px) !important;
}

.notification-modal .sr-panel-filters{
  border-top: 1px solid #d8d8d8;
  border-right: 1px solid #d8d8d8;
}

.notification-modal .sr-panel-mid-section{
  margin-bottom: 0 !important;
}

.notification-modal .ag-root-wrapper-body.ag-layout-normal{
  border: 1px solid #d8d8d8;
  padding-top: 10px;
}

@media (max-width: 1300px) {
  .notification-modal .notifications-grid {
    height: calc(100vh - 430px) !important;
  }
}

@media only screen and (max-width: 1366px) and (min-width: 1360px) {
  .notification-modal .notifications-grid {
    height: calc(100vh - 230px) !important;
  }
}

</style>
